/**
 * Thin wrappers around the orval-generated audit log hooks (TRA-1626):
 * one page of the team's audit log under the filters, who is in the log
 * for the Who filter (TRA-1700), and the CSV export of the same filters
 * as a file download.
 */

import type { ApiRequestError } from '@/api/client'
import { fetchDownload } from '@/api/client'
import {
  getExportAuditEventsApiAuditEventsExportGetUrl,
  useListAuditActorsApiAuditEventsActorsGet,
  useListAuditEventsApiAuditEventsGet,
} from '@/api/generated/audit-events/audit-events'
import type {
  AuditEventListResponse,
  AuditEventResponse,
  ExportAuditEventsApiAuditEventsExportGetParams,
  ListAuditEventsApiAuditEventsGetParams,
} from '@/api/generated/model'

export type AuditEvent = AuditEventResponse
export type AuditEventParams = ListAuditEventsApiAuditEventsGetParams
export type AuditExportParams = ExportAuditEventsApiAuditEventsExportGetParams

interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  isFetching: boolean
  error: ApiRequestError | null
  isError: boolean
}

export function useAuditEvents(params: AuditEventParams): UnwrappedQuery<AuditEventListResponse> {
  // The last page stays on screen while the next loads, so paging never
  // flashes the empty state. A 403 is an answer, not a blip.
  const query = useListAuditEventsApiAuditEventsGet(params, {
    query: { placeholderData: (previous) => previous, retry: false },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/** The Who list: still loading, failed (typed text still filters), or ready. */
export type AuditActors =
  | { state: 'loading' }
  | { state: 'failed' }
  | { state: 'ready'; people: readonly string[] }

/**
 * Everyone in the team's log, for the Who list. One request per visit
 * (the default stale time), so paging never asks for it again.
 */
export function useAuditActors(): AuditActors {
  const query = useListAuditActorsApiAuditEventsActorsGet({ query: { retry: false } })
  if (query.isError) return { state: 'failed' }
  return query.data?.status === 200 ? { state: 'ready', people: query.data.data.actors } : { state: 'loading' }
}

export interface AuditExportResult {
  /** True when more rows matched than the file holds. */
  truncated: boolean
  /** The most rows a file holds, as the API says. */
  maxRows: number | null
}

/** The file name the API suggested, or a plain one. */
export function exportFileName(disposition: string | null): string {
  const match = /filename="([^"]+)"/.exec(disposition ?? '')
  return match?.[1] ?? 'audit-log.csv'
}

/**
 * Download the team's audit log as CSV under the filters. The browser
 * saves the file the API names; the result says whether the file is the
 * whole answer.
 */
export async function downloadAuditCsv(params: AuditExportParams): Promise<AuditExportResult> {
  const response = await fetchDownload(getExportAuditEventsApiAuditEventsExportGetUrl(params))
  const blob = await response.blob()
  const href = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.download = exportFileName(response.headers.get('Content-Disposition'))
  link.click()
  URL.revokeObjectURL(href)
  const maxRows = Number(response.headers.get('X-Export-Max-Rows'))
  return {
    truncated: response.headers.get('X-Export-Truncated') === 'true',
    maxRows: Number.isFinite(maxRows) && maxRows > 0 ? maxRows : null,
  }
}
